/**
 * Utility functions for formatting Pokémon names consistently across the application
 */

/**
 * Formats a Pokémon name for display, handling special cases like Unown forms
 * @param pokemonName - The Pokémon name (may include -shiny suffix)
 * @returns Formatted display name
 */
export const formatPokemonDisplayName = (pokemonName: string): string => {
  // Remove shiny suffix for display name
  const displayName = pokemonName.replace('-shiny', '');
  
  // Handle special Unown cases
  if (displayName === 'unown-exclamation') {
    return 'Unown !';
  }
  if (displayName === 'unown-question') {
    return 'Unown ?';
  }
  
  // Handle special Castform cases
  if (displayName === 'castform-rain') {
    return 'Castform (Rain)';
  }
  if (displayName === 'castform-sunny') {
    return 'Castform (Sunny)';
  }
  if (displayName === 'castform-hail') {
    return 'Castform (Hail)';
  }
  
  // Convert kebab-case to proper case
  return displayName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Checks if a search term matches a Pokémon name, including special Unown cases
 * @param pokemonName - The Pokémon name to search in
 * @param searchTerm - The search term
 * @returns True if the search term matches
 */
export const matchesPokemonSearch = (pokemonName: string, searchTerm: string): boolean => {
  const pokemonLower = pokemonName.toLowerCase();
  const searchLower = searchTerm.toLowerCase();
  
  // Check if the search term matches the Pokémon name directly
  if (pokemonLower.includes(searchLower)) {
    return true;
  }
  
  // Handle standalone ? and ! searches for Unown
  if (searchLower === '?' || searchLower === 'question') {
    return pokemonLower.includes('unown-question');
  }
  
  if (searchLower === '!' || searchLower === 'exclamation') {
    return pokemonLower.includes('unown-exclamation');
  }
  
  // Handle standalone weather searches for Castform
  if (searchLower === 'rain' || searchLower === 'rainy') {
    return pokemonLower.includes('castform-rain');
  }
  
  if (searchLower === 'sunny' || searchLower === 'sun') {
    return pokemonLower.includes('castform-sunny');
  }
  
  if (searchLower === 'hail' || searchLower === 'snow') {
    return pokemonLower.includes('castform-hail');
  }
  
  // Handle standalone parentheses searches for Castform
  if (searchLower === '(' || searchLower === ')' || searchLower === '()') {
    return pokemonLower.startsWith('castform');
  }
  
  // Handle special Unown cases for search
  if (searchLower.includes('unown')) {
    // Handle Unown question mark
    if (searchLower.includes('?') || searchLower.includes('question')) {
      return pokemonLower.includes('unown-question');
    }
    
    // Handle Unown exclamation mark
    if (searchLower.includes('!') || searchLower.includes('exclamation')) {
      return pokemonLower.includes('unown-exclamation');
    }
    
    // Handle Unown A-Z with both hyphen and space formats
    const unownLetterMatch = searchLower.match(/unown\s*([a-z]|\?|!)/);
    if (unownLetterMatch) {
      const letter = unownLetterMatch[1];
      if (letter === '?') {
        return pokemonLower.includes('unown-question');
      }
      if (letter === '!') {
        return pokemonLower.includes('unown-exclamation');
      }
      return pokemonLower.includes(`unown-${letter}`);
    }
    
    // If just "unown" or "unown " (with space), show all Unown forms
    if (searchLower === 'unown' || searchLower === 'unown ') {
      return pokemonLower.startsWith('unown-');
    }
  }
  
  // Handle special Castform cases for search
  if (searchLower.includes('castform')) {
    // Handle Castform weather forms with various formats
    if (searchLower.includes('rain') || searchLower.includes('rainy')) {
      return pokemonLower.includes('castform-rain');
    }
    
    if (searchLower.includes('sunny') || searchLower.includes('sun')) {
      return pokemonLower.includes('castform-sunny');
    }
    
    if (searchLower.includes('hail') || searchLower.includes('snow')) {
      return pokemonLower.includes('castform-hail');
    }
    
    // Handle various Castform formats with partial matching: "castform rain", "castform-rain", "castform (rain)", "castform-(rain)"
    const castformWeatherMatch = searchLower.match(/castform[\s\-\(]*([a-z]*)[\)]*/);
    if (castformWeatherMatch) {
      const weather = castformWeatherMatch[1];
      
      // Handle partial matches for hail
      if (weather === 'hail' || weather === 'snow' || weather.startsWith('hai') || weather.startsWith('sno')) {
        return pokemonLower.includes('castform-hail');
      }
      
      // Handle partial matches for rain
      if (weather === 'rain' || weather === 'rainy' || weather.startsWith('rai')) {
        return pokemonLower.includes('castform-rain');
      }
      
      // Handle partial matches for sunny
      if (weather === 'sunny' || weather === 'sun' || weather.startsWith('sun')) {
        return pokemonLower.includes('castform-sunny');
      }
      
      // Handle partial matches that could be any of them
      if (weather.length > 0) {
        // If the partial match could match multiple forms, show all castform forms
        if ((weather.startsWith('h') && weather.length <= 2) || 
            (weather.startsWith('r') && weather.length <= 2) || 
            (weather.startsWith('s') && weather.length <= 2)) {
          return pokemonLower.startsWith('castform');
        }
      }
    }
    
    // Handle partial matches with just "castform " + space or parentheses
    if (searchLower === 'castform ' || searchLower === 'castform (' || searchLower === 'castform-(') {
      return pokemonLower.startsWith('castform');
    }
    
    // If just "castform" or "castform " (with space), show all Castform forms
    if (searchLower === 'castform' || searchLower === 'castform ') {
      return pokemonLower.startsWith('castform');
    }
  }
  
  return false;
};
